/**
 * Market Map Editor Page
 * Create/Edit market maps with markers
 * Migrated to Leaflet (from Google Maps)
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  HiOutlineArrowLeft,
  HiOutlineSave,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlinePhotograph,
  HiOutlineLocationMarker,
  HiOutlineCheck,
  HiOutlineCursorClick,
  HiOutlineX,
  HiOutlineMenuAlt2,
  HiOutlineCollection,
  HiOutlineUpload,
  HiOutlineViewList,
} from 'react-icons/hi'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import marketMapService from '../../services/marketMapService'
import { AdvancedMapEditor } from '../../components/map'
import { UTILITY_ICONS } from '../../components/map/LeafletMarketMapEditor'
import { ExcelImportModal, UtilityImportModal, MarkerListEditor } from '../../components/market-map'

function MarketMapEditor() {
  const { mapId } = useParams()
  const navigate = useNavigate()
  const isEditMode = !!mapId
  const leafletMapRef = useRef(null)

  // Map data
  const [mapData, setMapData] = useState({
    name: '',
    description: '',
    map_type: 'cho_thue',
    center_lat: 16.0544,
    center_lng: 108.2022,
    zoom_level: 13,
    map_style: 'hybrid',
    access_level: 'public', // public | internal | private
    note_text: '',
    show_note: false,
  })

  // Markers
  const [markers, setMarkers] = useState([])
  const [selectedMarker, setSelectedMarker] = useState(null)
  const [showMarkerForm, setShowMarkerForm] = useState(false)

  // Utility Markers
  const [utilityMarkers, setUtilityMarkers] = useState([])
  const [selectedUtilityMarker, setSelectedUtilityMarker] = useState(null)
  const [showUtilityMarkerForm, setShowUtilityMarkerForm] = useState(false)
  const [addingUtilityMarker, setAddingUtilityMarker] = useState(false)
  const [repositioningUtilityMarker, setRepositioningUtilityMarker] = useState(false)
  const [utilityMarkerFormData, setUtilityMarkerFormData] = useState({
    name: '',
    latitude: '',
    longitude: '',
    icon_type: 'default',
  })
  const [markerFormData, setMarkerFormData] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    price_text: '',       // Display text (auto-generated from min/max)
    price_min: '',        // Min price (triệu đồng/m²)
    price_max: '',        // Max price (triệu đồng/m²)
    area_m2: '',          // Area in m²
    property_type: '',    // Property type: shophouse, biệt thự, đất nền, chung cư, condotel
    image_url: '',
    description: '',
    marker_color: '#EF4444',
    is_featured: false,
    show_label: true,     // Show card label on map
    // Hidden fields (kept for backward compatibility)
    price_value: '',
    area_text: '',
    bedrooms: '',
    bathrooms: '',
    property_status: '',
    external_url: '',
    contact_phone: '',
    contact_name: '',
    contact_email: '',
  })

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [addingMarker, setAddingMarker] = useState(false)
  const [addMarkerMode, setAddMarkerMode] = useState(null) // 'drag' | 'input' | null
  const [showAddMarkerModal, setShowAddMarkerModal] = useState(false)
  const [coordInput, setCoordInput] = useState('')
  const [showCenterMarker, setShowCenterMarker] = useState(true)
  // Mobile states
  const [showMobileSettings, setShowMobileSettings] = useState(false)
  const [showMobileMarkers, setShowMobileMarkers] = useState(false)

  // Excel import modal
  const [showExcelImport, setShowExcelImport] = useState(false)

  // Utility import modal
  const [showUtilityImport, setShowUtilityImport] = useState(false)

  // Chế độ sửa nhanh giá dạng danh sách
  const [showListEditor, setShowListEditor] = useState(false)

  // Layers for shape import
  const [layers, setLayers] = useState([])

  // Property types for selector
  const [propertyTypes, setPropertyTypes] = useState([
    { id: 'shophouse', name: 'Shophouse' },
    { id: 'biet_thu', name: 'Biệt thự' },
    { id: 'dat_nen', name: 'Đất nền' },
    { id: 'chung_cu', name: 'Chung cư' },
    { id: 'condotel', name: 'Condotel' },
  ])
  const [showAddPropertyType, setShowAddPropertyType] = useState(false)
  const [newPropertyTypeName, setNewPropertyTypeName] = useState('')
  const [showPropertyTypesModal, setShowPropertyTypesModal] = useState(false)
  const [editingPropertyType, setEditingPropertyType] = useState(null) // { id, name } or null

  // Load map data if editing
  useEffect(() => {
    if (isEditMode) {
      loadMapData()
    }
  }, [mapId])

  // Handle center change from map
  const handleCenterChange = useCallback((lat, lng) => {
    setMapData((prev) => ({
      ...prev,
      center_lat: lat,
      center_lng: lng,
    }))
    toast.success(`Đã đặt center: ${lat.toFixed(5)}, ${lng.toFixed(5)}`)
  }, [])

  // Handle zoom change from map
  const handleZoomChange = useCallback((zoom) => {
    setMapData((prev) => ({
      ...prev,
      zoom_level: zoom,
    }))
  }, [])

  // Handle marker click
  const handleMarkerClick = useCallback((markerData) => {
    setSelectedMarker(markerData)
    setMarkerFormData(markerData)
    setShowMarkerForm(true)
  }, [])

  // Handle temp marker confirm (for drag mode)
  // Keep temp marker visible while form is open so user can see position
  const handleTempMarkerConfirm = useCallback((lat, lng) => {
    setMarkerFormData((prev) => ({
      ...prev,
      latitude: lat.toFixed(8),
      longitude: lng.toFixed(8),
    }))
    setShowMarkerForm(true)
    // Keep addingMarker true to keep temp marker visible
    // setAddingMarker(false)
    setAddMarkerMode(null)
    setShowAddMarkerModal(false)
  }, [])

  const loadMapData = async () => {
    setLoading(true)
    try {
      const response = await marketMapService.getMapDetail(mapId)
      if (response.status === 'success') {
        const data = response.data
        setMapData({
          name: data.name || '',
          description: data.description || '',
          map_type: data.map_type || 'cho_thue',
          center_lat: data.center_lat || 16.0544,
          center_lng: data.center_lng || 108.2022,
          zoom_level: data.zoom_level || 13,
          map_style: data.map_style || 'hybrid',
          access_level: data.access_level || 'public',
          note_text: data.note_text || '',
          show_note: !!data.show_note,
        })
        setMarkers(data.markers || [])
        setUtilityMarkers(data.utility_markers || [])

        // Load layers for shape import
        try {
          const layersResponse = await marketMapService.getLayers(mapId)
          if (layersResponse.status === 'success') {
            setLayers(layersResponse.data || [])
          }
        } catch (layerError) {
          console.error('Failed to load layers:', layerError)
          // Non-critical, continue without layers
        }
      }
    } catch (error) {
      console.error('Failed to load map:', error)
      toast.error('Không thể tải dữ liệu')
    } finally {
      setLoading(false)
    }
  }

  // Utility marker handlers
  const handleUtilityMarkerClick = useCallback((markerData) => {
    setSelectedUtilityMarker(markerData)
    setUtilityMarkerFormData(markerData)
    setShowUtilityMarkerForm(true)
  }, [])

  const handleSaveUtilityMarker = async () => {
    if (!utilityMarkerFormData.name || !utilityMarkerFormData.latitude || !utilityMarkerFormData.longitude) {
      toast.error('Vui lòng nhập tên và tọa độ')
      return
    }

    try {
      let response
      if (selectedUtilityMarker?.id) {
        response = await marketMapService.updateUtilityMarker(
          mapId,
          selectedUtilityMarker.id,
          utilityMarkerFormData
        )
      } else {
        response = await marketMapService.createUtilityMarker(mapId, utilityMarkerFormData)
      }

      if (response.status === 'success') {
        toast.success(selectedUtilityMarker?.id ? 'Đã cập nhật!' : 'Đã thêm tiện ích!')
        if (leafletMapRef.current) {
          leafletMapRef.current.removeTempMarker()
        }
        setShowUtilityMarkerForm(false)
        setSelectedUtilityMarker(null)
        setAddingUtilityMarker(false)
        resetUtilityMarkerForm()
        loadMapData()
      }
    } catch (error) {
      console.error('Failed to save utility marker:', error)
      toast.error('Không thể lưu')
    }
  }

  const handleDeleteUtilityMarker = async (markerId) => {
    if (!confirm('Xóa tiện ích này?')) return

    try {
      const response = await marketMapService.deleteUtilityMarker(mapId, markerId)
      if (response.status === 'success') {
        toast.success('Đã xóa')
        setShowUtilityMarkerForm(false)
        setSelectedUtilityMarker(null)
        loadMapData()
      }
    } catch (error) {
      console.error('Failed to delete utility marker:', error)
      toast.error('Không thể xóa')
    }
  }

  const resetUtilityMarkerForm = () => {
    setUtilityMarkerFormData({
      name: '',
      latitude: '',
      longitude: '',
      icon_type: 'default',
    })
  }

  const startAddingUtilityMarker = () => {
    setSelectedUtilityMarker(null)
    resetUtilityMarkerForm()
    setAddingUtilityMarker(true)
    // Don't call createTempMarker() here - let the useEffect in LeafletMarketMapEditor
    // handle it after React re-renders with the correct onTempMarkerConfirm callback

    toast.success('Kéo marker đến vị trí mong muốn, sau đó click vào marker để xác nhận', { duration: 5000 })
  }

  const handleUtilityTempMarkerConfirm = useCallback((lat, lng) => {
    setUtilityMarkerFormData((prev) => ({
      ...prev,
      latitude: lat.toFixed(8),
      longitude: lng.toFixed(8),
    }))
    setShowUtilityMarkerForm(true)
    setAddingUtilityMarker(false)
    setRepositioningUtilityMarker(false)
  }, [])

  const handleSaveMap = async () => {
    if (!mapData.name.trim()) {
      toast.error('Vui lòng nhập tên bản đồ')
      return
    }

    setSaving(true)
    try {
      let response
      if (isEditMode) {
        response = await marketMapService.updateMap(mapId, mapData)
      } else {
        response = await marketMapService.createMap(mapData)
      }

      if (response.status === 'success') {
        toast.success(isEditMode ? 'Đã cập nhật!' : 'Đã tạo bản đồ!')
        if (!isEditMode) {
          navigate(`/admin/market-maps/${response.data.id}`)
        }
      }
    } catch (error) {
      console.error('Failed to save:', error)
      toast.error('Không thể lưu')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveMarker = async () => {
    if (!markerFormData.name || !markerFormData.latitude || !markerFormData.longitude) {
      toast.error('Vui lòng nhập tên và tọa độ')
      return
    }

    try {
      let response
      if (selectedMarker?.id) {
        response = await marketMapService.updateMarker(
          mapId,
          selectedMarker.id,
          markerFormData
        )
      } else {
        response = await marketMapService.createMarker(mapId, markerFormData)
      }

      if (response.status === 'success') {
        toast.success(selectedMarker?.id ? 'Đã cập nhật!' : 'Đã thêm marker!')
        // Clean up temp marker and state
        if (leafletMapRef.current) {
          leafletMapRef.current.removeTempMarker()
        }
        setShowMarkerForm(false)
        setSelectedMarker(null)
        setAddingMarker(false)
        resetMarkerForm()
        loadMapData()
      }
    } catch (error) {
      console.error('Failed to save marker:', error)
      toast.error('Không thể lưu marker')
    }
  }

  const handleDeleteMarker = async (markerId) => {
    if (!confirm('Xóa marker này?')) return

    try {
      const response = await marketMapService.deleteMarker(mapId, markerId)
      if (response.status === 'success') {
        toast.success('Đã xóa marker')
        setShowMarkerForm(false)
        setSelectedMarker(null)
        loadMapData()
      }
    } catch (error) {
      console.error('Failed to delete marker:', error)
      toast.error('Không thể xóa')
    }
  }

  const resetMarkerForm = () => {
    setMarkerFormData({
      name: '',
      address: '',
      latitude: '',
      longitude: '',
      price_text: '',
      price_min: '',
      price_max: '',
      area_m2: '',
      property_type: '',
      image_url: '',
      description: '',
      marker_color: '#EF4444',
      is_featured: false,
      show_label: true,
      // Hidden fields
      price_value: '',
      area_text: '',
      bedrooms: '',
      bathrooms: '',
      property_status: '',
      external_url: '',
      contact_phone: '',
      contact_name: '',
      contact_email: '',
    })
  }

  const startAddingMarker = () => {
    setShowAddMarkerModal(true)
    setSelectedMarker(null)
    resetMarkerForm()
    setCoordInput('')
  }

  // Chế độ 1: Kéo thả marker
  const startDragMode = () => {
    setShowAddMarkerModal(false)
    setAddMarkerMode('drag')
    setAddingMarker(true)

    // Tạo marker tạm ở giữa màn hình để kéo thả (Leaflet)
    if (leafletMapRef.current) {
      leafletMapRef.current.createTempMarker()
    }

    toast.success('Kéo marker đến vị trí mong muốn, sau đó click vào marker để xác nhận', { duration: 5000 })
  }

  // Chế độ 2: Nhập tọa độ
  const startInputMode = () => {
    setAddMarkerMode('input')
  }

  // Xác nhận vị trí marker
  // Keep temp marker visible while form is open
  const confirmMarkerPosition = (lat, lng) => {
    // Update temp marker position to confirmed location
    if (leafletMapRef.current && leafletMapRef.current.updateTempMarkerPosition) {
      leafletMapRef.current.updateTempMarkerPosition(lat, lng)
    }

    setMarkerFormData((prev) => ({
      ...prev,
      latitude: lat.toFixed(8),
      longitude: lng.toFixed(8),
    }))
    setShowMarkerForm(true)
    // Keep addingMarker true to keep temp marker visible
    // setAddingMarker(false)
    setAddMarkerMode(null)
    setShowAddMarkerModal(false)
  }

  // Xử lý nhập tọa độ (format: "16.049726, 108.2336816")
  const handleCoordSubmit = () => {
    const trimmed = coordInput.trim()
    // Hỗ trợ cả dấu phẩy và khoảng trắng
    const parts = trimmed.split(/[,\s]+/).filter(Boolean)

    if (parts.length !== 2) {
      toast.error('Định dạng không hợp lệ. VD: 16.049726, 108.2336816')
      return
    }

    const lat = parseFloat(parts[0])
    const lng = parseFloat(parts[1])

    if (isNaN(lat) || isNaN(lng)) {
      toast.error('Tọa độ không hợp lệ')
      return
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      toast.error('Tọa độ nằm ngoài phạm vi hợp lệ')
      return
    }

    // Pan map đến vị trí mới và tạo/cập nhật temp marker (Leaflet)
    if (leafletMapRef.current) {
      leafletMapRef.current.panTo(lat, lng)
      // Create or update temp marker at the coordinates
      leafletMapRef.current.updateTempMarkerPosition(lat, lng)
    }

    // Set addingMarker to true to keep temp marker visible
    setAddingMarker(true)
    confirmMarkerPosition(lat, lng)
  }

  // Hủy thêm marker
  const cancelAddMarker = () => {
    // Xóa marker tạm (Leaflet)
    if (leafletMapRef.current) {
      leafletMapRef.current.removeTempMarker()
    }
    setAddingMarker(false)
    setAddMarkerMode(null)
    setShowAddMarkerModal(false)
    setCoordInput('')
  }

  // Handle Excel import
  const handleExcelImport = async (markers) => {
    try {
      const response = await marketMapService.bulkCreateMarkers(mapId, markers)
      if (response.status === 'success') {
        toast.success(`Đã import ${markers.length} markers thành công!`)
        loadMapData() // Reload to show new markers
      }
    } catch (error) {
      console.error('Excel import error:', error)
      toast.error('Không thể import markers')
      throw error
    }
  }

  // Handle Utility markers import
  const handleUtilityImport = async (markers) => {
    try {
      const response = await marketMapService.bulkCreateUtilityMarkers(mapId, markers)
      if (response.status === 'success') {
        toast.success(`Đã import ${markers.length} tiện ích thành công!`)
        loadMapData() // Reload to show new utility markers
      }
    } catch (error) {
      console.error('Utility import error:', error)
      toast.error('Không thể import tiện ích')
      throw error
    }
  }

  // Handle Shapes import from KMZ
  const handleShapesImport = async (shapes, layerId, newLayerName) => {
    try {
      let targetLayerId = layerId

      // Create new layer if needed
      if (!targetLayerId) {
        const layerResponse = await marketMapService.createLayer(mapId, {
          name: newLayerName || 'KMZ Import',
          layer_type: 'custom',
          description: 'Imported from KMZ file',
        })
        if (layerResponse.status === 'success') {
          targetLayerId = layerResponse.data.id
        } else {
          throw new Error('Failed to create layer')
        }
      }

      // Add layer_id to each shape
      const shapesWithLayer = shapes.map(shape => ({
        ...shape,
        layer_id: targetLayerId,
      }))

      // Bulk create shapes
      const response = await marketMapService.bulkCreateShapes(mapId, shapesWithLayer)
      if (response.status === 'success') {
        toast.success(`Đã import ${shapes.length} shapes thành công!`)
        loadMapData() // Reload to show new layers
      }
    } catch (error) {
      console.error('Shapes import error:', error)
      toast.error('Không thể import shapes')
      throw error
    }
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={() => navigate('/admin/market-maps')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <HiOutlineArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">
              {isEditMode ? 'Chỉnh sửa bản đồ' : 'Tạo bản đồ mới'}
            </h1>
            {mapData.name && (
              <p className="text-xs sm:text-sm text-gray-500 truncate max-w-[120px] sm:max-w-none">{mapData.name}</p>
            )}
          </div>
        </div>

        {/* Desktop actions */}
        <div className="hidden sm:flex items-center gap-3">
          {isEditMode && (
            <>
              <button
                onClick={() => setShowExcelImport(true)}
                className="btn-outline"
                title="Import từ Excel"
              >
                <HiOutlineUpload className="w-5 h-5 mr-1" />
                Import File (KMZ, XLS)
              </button>
              <button
                onClick={startAddingMarker}
                className={clsx(
                  'btn-outline',
                  addingMarker && 'ring-2 ring-primary-500'
                )}
              >
                <HiOutlinePlus className="w-5 h-5 mr-1" />
                Thêm marker
              </button>
              <button
                onClick={() => setShowListEditor(true)}
                className="btn-outline"
                title="Sửa nhanh giá hàng loạt dạng danh sách"
              >
                <HiOutlineViewList className="w-5 h-5 mr-1" />
                Sửa nhanh (List)
              </button>
              <button
                onClick={startAddingUtilityMarker}
                className={clsx(
                  'btn-outline',
                  addingUtilityMarker && 'ring-2 ring-emerald-500'
                )}
                title="Thêm tiện ích xung quanh (trường học, bệnh viện, ...)"
              >
                <span className="mr-1">🏫</span>
                Tiện ích
              </button>
              <button
                onClick={() => setShowUtilityImport(true)}
                className="btn-outline text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                title="Import tiện ích từ dữ liệu paste"
              >
                <HiOutlineUpload className="w-5 h-5 mr-1" />
                Import Tiện ích
              </button>
            </>
          )}
          <button
            onClick={handleSaveMap}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <span className="spinner w-5 h-5 mr-1" />
            ) : (
              <HiOutlineSave className="w-5 h-5 mr-1" />
            )}
            Lưu bản đồ
          </button>
        </div>

        {/* Mobile actions */}
        <div className="flex sm:hidden items-center gap-1.5">
          {isEditMode && (
            <>
              <button
                onClick={() => setShowExcelImport(true)}
                className="p-2 rounded-lg transition-colors hover:bg-gray-100 text-gray-600"
                title="Import Excel"
              >
                <HiOutlineUpload className="w-5 h-5" />
              </button>
              <button
                onClick={startAddingMarker}
                className={clsx(
                  'p-2 rounded-lg transition-colors',
                  addingMarker ? 'bg-primary-100 text-primary-600' : 'hover:bg-gray-100 text-gray-600'
                )}
                title="Thêm marker"
              >
                <HiOutlinePlus className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowListEditor(true)}
                className="p-2 rounded-lg transition-colors hover:bg-gray-100 text-gray-600"
                title="Sửa nhanh (List)"
              >
                <HiOutlineViewList className="w-5 h-5" />
              </button>
            </>
          )}
          <button
            onClick={handleSaveMap}
            disabled={saving}
            className="p-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
            title="Lưu"
          >
            {saving ? (
              <span className="spinner w-5 h-5" />
            ) : (
              <HiOutlineSave className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden relative">
        {/* Mobile Bottom Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-2 flex gap-2 z-40 md:hidden">
          <button
            onClick={() => {
              setShowMobileSettings(!showMobileSettings)
              setShowMobileMarkers(false)
            }}
            className={clsx(
              'flex-1 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors',
              showMobileSettings ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
            )}
          >
            <HiOutlineMenuAlt2 className="w-5 h-5" />
            Cài đặt
          </button>
          <button
            onClick={() => {
              setShowMobileMarkers(!showMobileMarkers)
              setShowMobileSettings(false)
            }}
            className={clsx(
              'flex-1 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors',
              showMobileMarkers ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
            )}
          >
            <HiOutlineCollection className="w-5 h-5" />
            Markers ({markers.length})
          </button>
        </div>

        {/* Mobile Settings Panel */}
        {showMobileSettings && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowMobileSettings(false)} />
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] overflow-y-auto animate-slideUp">
              <div className="sticky top-0 bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Cài đặt bản đồ</h3>
                <button onClick={() => setShowMobileSettings(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <HiOutlineX className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-4 space-y-4 pb-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tên bản đồ *</label>
                  <input
                    type="text"
                    value={mapData.name}
                    onChange={(e) => setMapData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="VD: Giá căn hộ Đà Nẵng 2024"
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mô tả</label>
                  <textarea
                    value={mapData.description}
                    onChange={(e) => setMapData((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Mô tả ngắn về bản đồ..."
                    rows={2}
                    className="input"
                  />
                </div>
                {/* Ghi chú hiển thị trên map */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Ghi chú trên bản đồ</label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={mapData.show_note}
                        onChange={(e) => setMapData((prev) => ({ ...prev, show_note: e.target.checked }))}
                      />
                      Hiển thị
                    </label>
                  </div>
                  <textarea
                    value={mapData.note_text}
                    onChange={(e) => setMapData((prev) => ({ ...prev, note_text: e.target.value }))}
                    placeholder="VD: Dữ liệu giá bán được cập nhật từ nhiều nguồn và mang tính chất tham khảo."
                    rows={2}
                    className="input"
                  />
                  <p className="text-2xs text-gray-400 mt-1">Hiện ở góc phải-dưới bản đồ khi bật.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Loại bản đồ</label>
                    <select
                      value={mapData.map_type}
                      onChange={(e) => setMapData((prev) => ({ ...prev, map_type: e.target.value }))}
                      className="input"
                    >
                      <option value="cho_thue">Cho thuê</option>
                      <option value="mua_ban">Mua bán</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kiểu bản đồ</label>
                    <select
                      value={mapData.map_style}
                      onChange={(e) => {
                        setMapData((prev) => ({ ...prev, map_style: e.target.value }))
                        if (leafletMapRef.current) {
                          leafletMapRef.current.setMapStyle(e.target.value)
                        }
                      }}
                      className="input"
                    >
                      <option value="roadmap">Roadmap</option>
                      <option value="satellite">Satellite</option>
                      <option value="hybrid">Hybrid</option>
                      <option value="terrain">Terrain</option>
                    </select>
                  </div>
                </div>
                {/* Access Level - Mobile */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mức độ bảo mật</label>
                  <select
                    value={mapData.access_level}
                    onChange={(e) => setMapData((prev) => ({ ...prev, access_level: e.target.value }))}
                    className="input"
                  >
                    <option value="public">Công khai</option>
                    <option value="internal">Nội bộ (Users đăng nhập)</option>
                    <option value="private">Riêng tư (Leader, Admin, CEO)</option>
                  </select>
                </div>
                <div className="bg-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-500">Center:</span>
                    <span className="font-mono text-gray-900 text-xs">
                      {mapData.center_lat.toFixed(5)}, {mapData.center_lng.toFixed(5)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Zoom: {mapData.zoom_level}</span>
                    <input
                      type="range"
                      min="8"
                      max="18"
                      value={mapData.zoom_level}
                      onChange={(e) => {
                        const zoom = parseInt(e.target.value)
                        setMapData((prev) => ({ ...prev, zoom_level: zoom }))
                        if (leafletMapRef.current) {
                          leafletMapRef.current.setZoom(zoom)
                        }
                      }}
                      className="w-24 h-1.5 accent-primary-600"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Markers Panel */}
        {showMobileMarkers && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowMobileMarkers(false)} />
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] overflow-y-auto animate-slideUp">
              <div className="sticky top-0 bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Markers ({markers.length})</h3>
                <button onClick={() => setShowMobileMarkers(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <HiOutlineX className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              {markers.length > 0 ? (
                <div className="divide-y divide-gray-100 pb-8">
                  {markers.map((marker) => (
                    <button
                      key={marker.id}
                      onClick={() => {
                        setSelectedMarker(marker)
                        setMarkerFormData(marker)
                        setShowMarkerForm(true)
                        setShowMobileMarkers(false)
                      }}
                      className={clsx(
                        'w-full p-4 text-left hover:bg-gray-50 transition-colors flex gap-3',
                        selectedMarker?.id === marker.id && 'bg-primary-50'
                      )}
                    >
                      <div
                        className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
                        style={{ backgroundColor: marker.marker_color || '#EF4444' }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm truncate">{marker.name}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {marker.address || `${marker.latitude}, ${marker.longitude}`}
                        </div>
                        <div className="text-sm text-primary-600 font-medium mt-0.5">{marker.price_text || '-'}</div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500 text-sm">
                  {isEditMode ? 'Chưa có marker. Nhấn + để thêm.' : 'Lưu bản đồ trước để thêm markers.'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Desktop Sidebar */}
        <div className="hidden md:flex w-80 bg-gray-50 border-r border-gray-200 flex-col overflow-hidden">
          {/* Map Settings */}
          <div className="p-4 border-b border-gray-200 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tên bản đồ *
              </label>
              <input
                type="text"
                value={mapData.name}
                onChange={(e) =>
                  setMapData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="VD: Giá căn hộ Đà Nẵng 2024"
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mô tả
              </label>
              <textarea
                value={mapData.description}
                onChange={(e) =>
                  setMapData((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Mô tả ngắn về bản đồ..."
                rows={2}
                className="input"
              />
            </div>

            {/* Ghi chú hiển thị trên map */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Ghi chú trên bản đồ
                </label>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mapData.show_note}
                    onChange={(e) =>
                      setMapData((prev) => ({ ...prev, show_note: e.target.checked }))
                    }
                  />
                  Hiển thị
                </label>
              </div>
              <textarea
                value={mapData.note_text}
                onChange={(e) =>
                  setMapData((prev) => ({ ...prev, note_text: e.target.value }))
                }
                placeholder="VD: Dữ liệu giá bán được cập nhật từ nhiều nguồn và mang tính chất tham khảo."
                rows={2}
                className="input"
              />
              <p className="text-2xs text-gray-400 mt-1">
                Hiện ở góc phải-dưới bản đồ khi bật.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Loại bản đồ
              </label>
              <select
                value={mapData.map_type}
                onChange={(e) =>
                  setMapData((prev) => ({ ...prev, map_type: e.target.value }))
                }
                className="input"
              >
                <option value="cho_thue">Cho thuê</option>
                <option value="mua_ban">Mua bán</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kiểu bản đồ
              </label>
              <select
                value={mapData.map_style}
                onChange={(e) => {
                  setMapData((prev) => ({ ...prev, map_style: e.target.value }))
                  if (leafletMapRef.current) {
                    leafletMapRef.current.setMapStyle(e.target.value)
                  }
                }}
                className="input"
              >
                <option value="roadmap">Roadmap</option>
                <option value="satellite">Satellite</option>
                <option value="hybrid">Hybrid</option>
                <option value="terrain">Terrain</option>
              </select>
            </div>

            {/* Access Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mức độ bảo mật
              </label>
              <select
                value={mapData.access_level}
                onChange={(e) => setMapData((prev) => ({ ...prev, access_level: e.target.value }))}
                className="input"
              >
                <option value="public">Không bảo mật (Ai cũng xem được)</option>
                <option value="internal">Nội bộ (Users đăng nhập)</option>
                <option value="private">Riêng tư (Leader, Admin, CEO)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {mapData.access_level === 'public' && 'Tất cả mọi người có thể xem'}
                {mapData.access_level === 'internal' && 'Chỉ users đăng nhập mới xem được'}
                {mapData.access_level === 'private' && 'Chỉ Leader, Admin, CEO mới xem được'}
              </p>
            </div>

            {/* Center & Zoom Settings */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 text-sm flex items-center gap-2">
                  <HiOutlineCursorClick className="w-4 h-4" />
                  Vị trí mặc định
                </h4>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showCenterMarker}
                    onChange={(e) => setShowCenterMarker(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600"
                  />
                  <span className="text-gray-600">Hiện ghim</span>
                </label>
              </div>

              <div className="bg-gray-100 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Center:</span>
                  <span className="font-mono text-gray-900 text-xs">
                    {mapData.center_lat.toFixed(5)}, {mapData.center_lng.toFixed(5)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Zoom:</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="8"
                      max="18"
                      value={mapData.zoom_level}
                      onChange={(e) => {
                        const zoom = parseInt(e.target.value)
                        setMapData((prev) => ({ ...prev, zoom_level: zoom }))
                        if (leafletMapRef.current) {
                          leafletMapRef.current.setZoom(zoom)
                        }
                      }}
                      className="w-20 h-1.5 accent-primary-600"
                    />
                    <span className="font-mono text-gray-900 w-6 text-right">
                      {mapData.zoom_level}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-3 space-y-2">
                <button
                  onClick={() => {
                    if (leafletMapRef.current) {
                      const center = leafletMapRef.current.getCenter()
                      const zoom = leafletMapRef.current.getZoom()
                      if (center) {
                        setMapData((prev) => ({
                          ...prev,
                          center_lat: center.lat,
                          center_lng: center.lng,
                          zoom_level: zoom,
                        }))
                        leafletMapRef.current.setCenterMarkerPosition(center.lat, center.lng)
                        toast.success('Đã lưu vị trí hiện tại làm mặc định')
                      }
                    }
                  }}
                  className="w-full btn-outline text-xs py-2"
                >
                  <HiOutlineCursorClick className="w-4 h-4 mr-1" />
                  Đặt vị trí hiện tại làm mặc định
                </button>

                <p className="text-xs text-gray-500 text-center">
                  💡 Kéo ghim xanh trên bản đồ để đặt center
                </p>
              </div>
            </div>
          </div>

          {/* Markers List */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-3 bg-gray-100 border-b border-gray-200">
              <h3 className="font-medium text-gray-900 text-sm">
                Markers ({markers.length})
              </h3>
            </div>

            {markers.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {markers.map((marker) => (
                  <button
                    key={marker.id}
                    onClick={() => {
                      setSelectedMarker(marker)
                      setMarkerFormData(marker)
                      setShowMarkerForm(true)
                    }}
                    className={clsx(
                      'w-full p-3 text-left hover:bg-gray-100 transition-colors flex gap-3',
                      selectedMarker?.id === marker.id && 'bg-primary-50'
                    )}
                  >
                    <div
                      className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                      style={{ backgroundColor: marker.marker_color || '#EF4444' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm truncate">
                        {marker.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {marker.address || `${marker.latitude}, ${marker.longitude}`}
                      </div>
                      <div className="text-sm text-primary-600 font-medium mt-0.5">
                        {marker.price_text || '-'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500 text-sm">
                {isEditMode
                  ? 'Chưa có marker. Click "Thêm marker" để bắt đầu.'
                  : 'Lưu bản đồ trước để thêm markers.'}
              </div>
            )}
          </div>
        </div>

        {/* Map Area */}
        <div className="flex-1 relative">
          {/* Advanced Map Editor with Drawing Tools */}
          <AdvancedMapEditor
            ref={leafletMapRef}
            mapId={mapId ? parseInt(mapId) : null}
            markers={markers}
            utilityMarkers={utilityMarkers}
            center={{ lat: mapData.center_lat, lng: mapData.center_lng }}
            zoom={mapData.zoom_level}
            mapStyle={mapData.map_style}
            showCenterMarker={showCenterMarker}
            onCenterChange={handleCenterChange}
            onZoomChange={handleZoomChange}
            onMarkerClick={handleMarkerClick}
            onUtilityMarkerClick={handleUtilityMarkerClick}
            onTempMarkerConfirm={(addingUtilityMarker || repositioningUtilityMarker) ? handleUtilityTempMarkerConfirm : handleTempMarkerConfirm}
            addingMarker={addingMarker || addingUtilityMarker || repositioningUtilityMarker}
          />

          {/* Adding marker overlay - Drag mode */}
          {addingMarker && addMarkerMode === 'drag' && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm flex items-center gap-3">
              <HiOutlineLocationMarker className="w-5 h-5 animate-bounce" />
              <div>
                <div className="font-medium">Kéo marker xanh đến vị trí mong muốn</div>
                <div className="text-xs text-emerald-200">Click vào marker để xác nhận</div>
              </div>
              <button
                onClick={cancelAddMarker}
                className="ml-2 hover:bg-emerald-700 p-1.5 rounded"
              >
                ✕
              </button>
            </div>
          )}

          {/* Modal chọn chế độ thêm marker */}
          {showAddMarkerModal && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Thêm marker mới</h3>
                  <button
                    onClick={cancelAddMarker}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    ✕
                  </button>
                </div>

                <div className="p-4 space-y-3">
                  {/* Chế độ 1: Kéo thả */}
                  {addMarkerMode !== 'input' && (
                    <button
                      onClick={startDragMode}
                      className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-left group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                          <HiOutlineLocationMarker className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Kéo thả trên bản đồ</div>
                          <div className="text-sm text-gray-500">Kéo marker đến vị trí mong muốn</div>
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Chế độ 2: Nhập tọa độ */}
                  <button
                    onClick={startInputMode}
                    className={clsx(
                      'w-full p-4 border-2 rounded-lg transition-colors text-left group',
                      addMarkerMode === 'input'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-primary-500 hover:bg-primary-50'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={clsx(
                        'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                        addMarkerMode === 'input' ? 'bg-primary-200' : 'bg-primary-100 group-hover:bg-primary-200'
                      )}>
                        <span className="text-primary-600 font-mono text-sm font-bold">XY</span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Nhập tọa độ</div>
                        <div className="text-sm text-gray-500">VD: 16.049726, 108.2336816</div>
                      </div>
                    </div>
                  </button>

                  {/* Input tọa độ khi chọn chế độ input */}
                  {addMarkerMode === 'input' && (
                    <div className="mt-4 space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nhập tọa độ (Latitude, Longitude)
                        </label>
                        <input
                          type="text"
                          value={coordInput}
                          onChange={(e) => setCoordInput(e.target.value)}
                          placeholder="16.049726, 108.2336816"
                          className="input font-mono"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCoordSubmit()
                            if (e.key === 'Escape') cancelAddMarker()
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Hỗ trợ định dạng: "16.049726, 108.2336816" hoặc "16.049726 108.2336816"
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => setAddMarkerMode(null)}
                          className="btn-outline flex-1"
                        >
                          Quay lại
                        </button>
                        <button
                          onClick={handleCoordSubmit}
                          disabled={!coordInput.trim()}
                          className="btn-primary flex-1"
                        >
                          Xác nhận
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Marker Form Panel */}
        {showMarkerForm && (
          <div className={clsx(
            'bg-white flex flex-col overflow-hidden',
            // Mobile: full screen modal
            'fixed inset-0 z-50 md:relative md:inset-auto md:z-auto',
            // Desktop: sidebar
            'md:w-96 md:border-l md:border-gray-200'
          )}>
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {selectedMarker?.id ? 'Chỉnh sửa marker' : 'Thêm marker mới'}
              </h3>
              <button
                onClick={() => {
                  // Clean up temp marker when closing form
                  if (leafletMapRef.current) {
                    leafletMapRef.current.removeTempMarker()
                  }
                  setShowMarkerForm(false)
                  setSelectedMarker(null)
                  setAddingMarker(false)
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <HiOutlineX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Basic Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên *
                </label>
                <input
                  type="text"
                  value={markerFormData.name}
                  onChange={(e) =>
                    setMarkerFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Tên tòa nhà/căn hộ"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Địa chỉ
                </label>
                <input
                  type="text"
                  value={markerFormData.address}
                  onChange={(e) =>
                    setMarkerFormData((prev) => ({ ...prev, address: e.target.value }))
                  }
                  placeholder="Địa chỉ đầy đủ"
                  className="input"
                />
              </div>

              {/* Coordinates */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitude *
                  </label>
                  <input
                    type="text"
                    value={markerFormData.latitude}
                    onChange={(e) =>
                      setMarkerFormData((prev) => ({
                        ...prev,
                        latitude: e.target.value,
                      }))
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitude *
                  </label>
                  <input
                    type="text"
                    value={markerFormData.longitude}
                    onChange={(e) =>
                      setMarkerFormData((prev) => ({
                        ...prev,
                        longitude: e.target.value,
                      }))
                    }
                    className="input"
                  />
                </div>
              </div>

              {/* Property Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loại BĐS
                </label>
                <div className="space-y-2">
                  <select
                    value={markerFormData.property_type}
                    onChange={(e) =>
                      setMarkerFormData((prev) => ({
                        ...prev,
                        property_type: e.target.value,
                      }))
                    }
                    className={clsx(
                      'input',
                      markerFormData.property_type &&
                        !propertyTypes.some((t) => t.id === markerFormData.property_type) &&
                        'border-orange-400 bg-orange-50'
                    )}
                  >
                    <option value="">-- Chọn loại --</option>
                    {/* Show warning option if property type no longer exists */}
                    {markerFormData.property_type &&
                      !propertyTypes.some((t) => t.id === markerFormData.property_type) && (
                        <option value={markerFormData.property_type} className="text-orange-600">
                          ⚠️ {markerFormData.property_type} (đã xóa)
                        </option>
                      )}
                    {propertyTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {/* Add new property type */}
                  {showAddPropertyType ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newPropertyTypeName}
                        onChange={(e) => setNewPropertyTypeName(e.target.value)}
                        placeholder="Tên loại mới..."
                        className="input flex-1"
                        autoFocus
                      />
                      <button
                        onClick={() => {
                          if (newPropertyTypeName.trim()) {
                            const newId = newPropertyTypeName.toLowerCase().replace(/\s+/g, '_')
                            setPropertyTypes((prev) => [...prev, { id: newId, name: newPropertyTypeName.trim() }])
                            setMarkerFormData((prev) => ({ ...prev, property_type: newId }))
                            setNewPropertyTypeName('')
                            setShowAddPropertyType(false)
                            toast.success('Đã thêm loại BĐS mới')
                          }
                        }}
                        className="px-3 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
                      >
                        Lưu
                      </button>
                      <button
                        onClick={() => {
                          setNewPropertyTypeName('')
                          setShowAddPropertyType(false)
                        }}
                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
                      >
                        Hủy
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowAddPropertyType(true)}
                        className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                        <HiOutlinePlus className="w-3 h-3" />
                        Thêm loại mới
                      </button>
                      <button
                        onClick={() => setShowPropertyTypesModal(true)}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                      >
                        <HiOutlineCollection className="w-3 h-3" />
                        Quản lý
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Range (triệu đồng/m²) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Giá (triệu đồng/m²)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <input
                      type="number"
                      value={markerFormData.price_min}
                      onChange={(e) => {
                        const min = e.target.value
                        const max = markerFormData.price_max
                        const priceText = min && max ? `${min}-${max} tr/m²` : min ? `${min} tr/m²` : ''
                        setMarkerFormData((prev) => ({
                          ...prev,
                          price_min: min,
                          price_text: priceText,
                        }))
                      }}
                      placeholder="Min"
                      className="input pr-12"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">tr/m²</span>
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      value={markerFormData.price_max}
                      onChange={(e) => {
                        const max = e.target.value
                        const min = markerFormData.price_min
                        const priceText = min && max ? `${min}-${max} tr/m²` : max ? `${max} tr/m²` : ''
                        setMarkerFormData((prev) => ({
                          ...prev,
                          price_max: max,
                          price_text: priceText,
                        }))
                      }}
                      placeholder="Max"
                      className="input pr-12"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">tr/m²</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Hiển thị: {markerFormData.price_text || '(chưa có)'}
                </p>
              </div>

              {/* Area (m²) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Diện tích (m²)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={markerFormData.area_m2}
                    onChange={(e) =>
                      setMarkerFormData((prev) => ({
                        ...prev,
                        area_m2: e.target.value,
                      }))
                    }
                    placeholder="100"
                    className="input pr-10"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">m²</span>
                </div>
              </div>

              {/* Hidden fields - kept for backward compatibility */}
              {/*
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phòng ngủ
                  </label>
                  <input
                    type="number"
                    value={markerFormData.bedrooms}
                    onChange={(e) =>
                      setMarkerFormData((prev) => ({
                        ...prev,
                        bedrooms: e.target.value,
                      }))
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phòng tắm
                  </label>
                  <input
                    type="number"
                    value={markerFormData.bathrooms}
                    onChange={(e) =>
                      setMarkerFormData((prev) => ({
                        ...prev,
                        bathrooms: e.target.value,
                      }))
                    }
                    className="input"
                  />
                </div>
              </div>
              */}

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL hình ảnh
                </label>
                <input
                  type="text"
                  value={markerFormData.image_url}
                  onChange={(e) =>
                    setMarkerFormData((prev) => ({
                      ...prev,
                      image_url: e.target.value,
                    }))
                  }
                  placeholder="https://..."
                  className="input"
                />
                {markerFormData.image_url && (
                  <img
                    src={markerFormData.image_url}
                    alt="Preview"
                    className="mt-2 w-full h-32 object-cover rounded-lg"
                  />
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mô tả
                </label>
                <textarea
                  value={markerFormData.description}
                  onChange={(e) =>
                    setMarkerFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={3}
                  className="input"
                />
              </div>

              {/* Contact - Hidden for simplicity */}
              {/*
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 text-sm mb-3">
                  Thông tin liên hệ
                </h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={markerFormData.contact_name}
                    onChange={(e) =>
                      setMarkerFormData((prev) => ({
                        ...prev,
                        contact_name: e.target.value,
                      }))
                    }
                    placeholder="Tên người liên hệ"
                    className="input"
                  />
                  <input
                    type="text"
                    value={markerFormData.contact_phone}
                    onChange={(e) =>
                      setMarkerFormData((prev) => ({
                        ...prev,
                        contact_phone: e.target.value,
                      }))
                    }
                    placeholder="Số điện thoại"
                    className="input"
                  />
                  <input
                    type="email"
                    value={markerFormData.contact_email}
                    onChange={(e) =>
                      setMarkerFormData((prev) => ({
                        ...prev,
                        contact_email: e.target.value,
                      }))
                    }
                    placeholder="Email"
                    className="input"
                  />
                </div>
              </div>
              */}

              {/* External URL - Hidden for simplicity */}
              {/*
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link chi tiết
                </label>
                <input
                  type="text"
                  value={markerFormData.external_url}
                  onChange={(e) =>
                    setMarkerFormData((prev) => ({
                      ...prev,
                      external_url: e.target.value,
                    }))
                  }
                  placeholder="https://..."
                  className="input"
                />
              </div>
              */}

              {/* Marker Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Màu marker
                </label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    '#EF4444', // red
                    '#F97316', // orange
                    '#EAB308', // yellow
                    '#22C55E', // green
                    '#06B6D4', // cyan
                    '#3B82F6', // blue
                    '#8B5CF6', // purple
                    '#EC4899', // pink
                  ].map((color) => (
                    <button
                      key={color}
                      onClick={() =>
                        setMarkerFormData((prev) => ({
                          ...prev,
                          marker_color: color,
                        }))
                      }
                      className={clsx(
                        'w-8 h-8 rounded-full transition-transform',
                        markerFormData.marker_color === color &&
                          'ring-2 ring-offset-2 ring-gray-400 scale-110'
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Featured */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={markerFormData.is_featured}
                  onChange={(e) =>
                    setMarkerFormData((prev) => ({
                      ...prev,
                      is_featured: e.target.checked,
                    }))
                  }
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Đánh dấu nổi bật</span>
              </label>

              {/* Show Label */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={markerFormData.show_label !== false}
                  onChange={(e) =>
                    setMarkerFormData((prev) => ({
                      ...prev,
                      show_label: e.target.checked,
                    }))
                  }
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Hiển thị card trên bản đồ</span>
              </label>
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-gray-200 flex gap-2">
              {selectedMarker?.id && (
                <button
                  onClick={() => handleDeleteMarker(selectedMarker.id)}
                  className="btn-outline text-red-600 border-red-200 hover:bg-red-50"
                >
                  <HiOutlineTrash className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => {
                  // Clean up temp marker when canceling
                  if (leafletMapRef.current) {
                    leafletMapRef.current.removeTempMarker()
                  }
                  setShowMarkerForm(false)
                  setSelectedMarker(null)
                  setAddingMarker(false)
                }}
                className="btn-outline flex-1"
              >
                Hủy
              </button>
              <button onClick={handleSaveMarker} className="btn-primary flex-1">
                <HiOutlineCheck className="w-4 h-4 mr-1" />
                Lưu
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Utility Marker Form Panel */}
      {showUtilityMarkerForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <span>🏫</span>
                {selectedUtilityMarker?.id ? 'Chỉnh sửa tiện ích' : 'Thêm tiện ích mới'}
              </h3>
              <button
                onClick={() => {
                  if (leafletMapRef.current) {
                    leafletMapRef.current.removeTempMarker()
                  }
                  setShowUtilityMarkerForm(false)
                  setSelectedUtilityMarker(null)
                  setAddingUtilityMarker(false)
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <HiOutlineX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên tiện ích *
                </label>
                <input
                  type="text"
                  value={utilityMarkerFormData.name}
                  onChange={(e) =>
                    setUtilityMarkerFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="VD: Trường THPT Phan Châu Trinh"
                  className="input"
                />
              </div>

              {/* Coordinates */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitude *
                  </label>
                  <input
                    type="text"
                    value={utilityMarkerFormData.latitude}
                    onChange={(e) =>
                      setUtilityMarkerFormData((prev) => ({
                        ...prev,
                        latitude: e.target.value,
                      }))
                    }
                    className="input font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitude *
                  </label>
                  <input
                    type="text"
                    value={utilityMarkerFormData.longitude}
                    onChange={(e) =>
                      setUtilityMarkerFormData((prev) => ({
                        ...prev,
                        longitude: e.target.value,
                      }))
                    }
                    className="input font-mono text-sm"
                  />
                </div>
              </div>

              {/* Reposition button - only show when editing existing marker */}
              {selectedUtilityMarker?.id && (
                <button
                  type="button"
                  onClick={() => {
                    // Close form temporarily, create temp marker at current position
                    setShowUtilityMarkerForm(false)
                    setRepositioningUtilityMarker(true)

                    // Create temp marker at current position for dragging
                    if (leafletMapRef.current) {
                      const lat = parseFloat(utilityMarkerFormData.latitude)
                      const lng = parseFloat(utilityMarkerFormData.longitude)
                      if (!isNaN(lat) && !isNaN(lng)) {
                        leafletMapRef.current.createTempMarker(lat, lng)
                        toast.success('Kéo marker đến vị trí mới, sau đó click để xác nhận', { duration: 5000 })
                      }
                    }
                  }}
                  className="w-full btn-outline text-primary-600 border-primary-200 hover:bg-primary-50 flex items-center justify-center gap-2"
                >
                  <HiOutlineLocationMarker className="w-4 h-4" />
                  Chỉnh sửa vị trí trên bản đồ
                </button>
              )}

              {/* Icon Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chọn icon
                </label>
                <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto p-2 bg-gray-50 rounded-lg">
                  {Object.entries(UTILITY_ICONS).map(([key, emoji]) => (
                    <button
                      key={key}
                      onClick={() =>
                        setUtilityMarkerFormData((prev) => ({ ...prev, icon_type: key }))
                      }
                      className={clsx(
                        'w-10 h-10 rounded-lg flex items-center justify-center text-xl transition-all',
                        utilityMarkerFormData.icon_type === key
                          ? 'bg-primary-100 ring-2 ring-primary-500 scale-110'
                          : 'bg-white hover:bg-gray-100 border border-gray-200'
                      )}
                      title={key.replace(/_/g, ' ')}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Đã chọn: {utilityMarkerFormData.icon_type.replace(/_/g, ' ')}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-gray-200 flex gap-2">
              {selectedUtilityMarker?.id && (
                <button
                  onClick={() => handleDeleteUtilityMarker(selectedUtilityMarker.id)}
                  className="btn-outline text-red-600 border-red-200 hover:bg-red-50"
                >
                  <HiOutlineTrash className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => {
                  if (leafletMapRef.current) {
                    leafletMapRef.current.removeTempMarker()
                  }
                  setShowUtilityMarkerForm(false)
                  setSelectedUtilityMarker(null)
                  setAddingUtilityMarker(false)
                }}
                className="btn-outline flex-1"
              >
                Hủy
              </button>
              <button onClick={handleSaveUtilityMarker} className="btn-primary flex-1">
                <HiOutlineCheck className="w-4 h-4 mr-1" />
                Lưu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adding utility marker overlay */}
      {addingUtilityMarker && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm flex items-center gap-3 z-50">
          <span className="text-xl">🏫</span>
          <div>
            <div className="font-medium">Thêm tiện ích</div>
            <div className="text-xs text-emerald-200">Kéo marker đến vị trí, click để xác nhận</div>
          </div>
          <button
            onClick={() => {
              if (leafletMapRef.current) {
                leafletMapRef.current.removeTempMarker()
              }
              setAddingUtilityMarker(false)
            }}
            className="ml-2 hover:bg-emerald-700 p-1.5 rounded"
          >
            ✕
          </button>
        </div>
      )}

      {/* Repositioning utility marker overlay */}
      {repositioningUtilityMarker && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 bg-primary-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm flex items-center gap-3 z-50">
          <HiOutlineLocationMarker className="w-5 h-5 animate-bounce" />
          <div>
            <div className="font-medium">Chỉnh sửa vị trí tiện ích</div>
            <div className="text-xs text-primary-200">Kéo marker đến vị trí mới, click để xác nhận</div>
          </div>
          <button
            onClick={() => {
              if (leafletMapRef.current) {
                leafletMapRef.current.removeTempMarker()
              }
              setRepositioningUtilityMarker(false)
              setShowUtilityMarkerForm(true) // Reopen the form
            }}
            className="ml-2 hover:bg-primary-700 p-1.5 rounded"
          >
            ✕
          </button>
        </div>
      )}

      {/* Property Types CRUD Modal */}
      {showPropertyTypesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <HiOutlineCollection className="w-5 h-5 text-primary-500" />
                Quản lý loại BĐS
              </h3>
              <button
                onClick={() => {
                  setShowPropertyTypesModal(false)
                  setEditingPropertyType(null)
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <HiOutlineX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {/* List of property types */}
              <div className="space-y-2">
                {propertyTypes.map((type) => (
                  <div
                    key={type.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group"
                  >
                    {editingPropertyType?.id === type.id ? (
                      /* Editing mode */
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={editingPropertyType.name}
                          onChange={(e) =>
                            setEditingPropertyType((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          className="input flex-1"
                          autoFocus
                        />
                        <button
                          onClick={() => {
                            if (editingPropertyType.name.trim()) {
                              setPropertyTypes((prev) =>
                                prev.map((t) =>
                                  t.id === editingPropertyType.id
                                    ? { ...t, name: editingPropertyType.name.trim() }
                                    : t
                                )
                              )
                              setEditingPropertyType(null)
                              toast.success('Đã cập nhật loại BĐS')
                            }
                          }}
                          className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                        >
                          <HiOutlineCheck className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingPropertyType(null)}
                          className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                        >
                          <HiOutlineX className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      /* View mode */
                      <>
                        <span className="text-sm font-medium text-gray-700">
                          {type.name}
                        </span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setEditingPropertyType({ id: type.id, name: type.name })}
                            className="p-1.5 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-700"
                            title="Chỉnh sửa"
                          >
                            <HiOutlinePencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Xóa loại "${type.name}"?`)) {
                                setPropertyTypes((prev) => prev.filter((t) => t.id !== type.id))
                                toast.success('Đã xóa loại BĐS')
                              }
                            }}
                            className="p-1.5 hover:bg-red-100 rounded text-gray-500 hover:text-red-600"
                            title="Xóa"
                          >
                            <HiOutlineTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {propertyTypes.length === 0 && (
                  <p className="text-center text-gray-500 text-sm py-4">
                    Chưa có loại BĐS nào
                  </p>
                )}
              </div>

              {/* Add new section */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPropertyTypeName}
                    onChange={(e) => setNewPropertyTypeName(e.target.value)}
                    placeholder="Thêm loại mới..."
                    className="input flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newPropertyTypeName.trim()) {
                        const newId = newPropertyTypeName.toLowerCase().replace(/\s+/g, '_')
                        if (propertyTypes.some((t) => t.id === newId)) {
                          toast.error('Loại BĐS này đã tồn tại')
                          return
                        }
                        setPropertyTypes((prev) => [...prev, { id: newId, name: newPropertyTypeName.trim() }])
                        setNewPropertyTypeName('')
                        toast.success('Đã thêm loại BĐS mới')
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      if (newPropertyTypeName.trim()) {
                        const newId = newPropertyTypeName.toLowerCase().replace(/\s+/g, '_')
                        if (propertyTypes.some((t) => t.id === newId)) {
                          toast.error('Loại BĐS này đã tồn tại')
                          return
                        }
                        setPropertyTypes((prev) => [...prev, { id: newId, name: newPropertyTypeName.trim() }])
                        setNewPropertyTypeName('')
                        toast.success('Đã thêm loại BĐS mới')
                      }
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 flex items-center gap-1"
                  >
                    <HiOutlinePlus className="w-4 h-4" />
                    Thêm
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowPropertyTypesModal(false)
                  setEditingPropertyType(null)
                }}
                className="w-full btn-primary"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Import Modal */}
      <ExcelImportModal
        isOpen={showExcelImport}
        onClose={() => setShowExcelImport(false)}
        onImport={handleExcelImport}
        onImportShapes={handleShapesImport}
        layers={layers}
      />

      {/* Utility Import Modal */}
      <UtilityImportModal
        isOpen={showUtilityImport}
        onClose={() => setShowUtilityImport(false)}
        onImport={handleUtilityImport}
      />

      {/* Sửa nhanh giá dạng danh sách */}
      {showListEditor && (
        <MarkerListEditor
          mapId={mapId}
          markers={markers}
          onClose={() => setShowListEditor(false)}
          onSaved={loadMapData}
        />
      )}
    </div>
  )
}

export default MarketMapEditor
